<template>
    <div v-show="!isPublic" class="card">
        <div class="card-body">
            <div class="d-flex justify-content-between">
                <h3 class="card-label mb-2">{{ $t('events.participators') }} <span class="text-primary ms-2">{{guests.length}}</span></h3>
            </div>
            <p class="mb-2 fs-5">{{ $t('events.planning.add_participators') }}</p>
          <div class="d-flex mb-10">
            <!-- Select2 pour les participants -->
            <Select2
                :is-required="false"
                container-class="col"
                :multiple="true"
                :show-search="true"
                :selectClass="'form-select form-control-lg form-select-solid'"
                :name="'new_participator_'+id"
                :placeholder-text="$t('events.planning.new_participator_placeholder')"
                v-model="formData.guests"
                :isDisabled="isPublic"
                :IsInputGroup="true"
                icon="bi bi-plus"
                :clickAddon="openCreateParticipatorModal"
            >
              <option v-for="(participator, index) in participatorNotSelected" :key="index" :value="participator.id">
                {{ participator.firstname }} {{ participator.lastname }}
              </option>
            </Select2>

            <!-- Bouton Ajouter -->
            <div class="ms-3">
              <Button type="new" btn-class="btn-md" btn-text="events.planning.add" :disabled="isPublic" @click="addParticipator"></Button>
            </div>
          </div>

          <!-- Checkbox envoyer mail en dessous -->
          <div
              class="form-check form-check-inline mt-3"
              v-if="formData?.guests && formData?.guests?.length > 0"
          >
            <input
                class="form-check-input mt-2"
                type="checkbox"
                id="sendMailCheckbox"
                v-model="formData.send_mail"
                :disabled="isPublic"
            >
            <label class="form-check-label" for="sendMailCheckbox">
              {{ $t('events.planning.send_mail') }}
            </label>
          </div>


          <div class="table-responsive mt-10" style="max-height: 400px; overflow-y: auto;">
              <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4">
                <thead style="background-color: #F2F3F4;">
                  <tr>
                    <th>{{$t('events.fullname')}}</th>
                    <th>{{$t('events.email')}}</th>
                    <th class="w-100px">{{$t('events.actions')}}</th>
                  </tr>
                </thead>
                <tbody v-if="detail.atelier_guests?.length > 0">
                  <tr v-for="participator in detail.atelier_guests" :key="participator.id">
                    <td>{{ participator.firstname + ' ' + participator.lastname }}</td>
                    <td>{{ participator.email }}</td>
                    <td class="d-flex w-100px">
                      <div class="dropdown me-5">
                        <span :title="$t('events.send_message')" v-if="!isPublic" style="cursor: pointer;" data-bs-toggle="dropdown" aria-expanded="false">
                          <i class="fa fa-paper-plane fs-2"></i>
                        </span>
                        <ul class="dropdown-menu">
                            <li style="cursor: pointer;" @click="addGuestsByMail(participator.id)"><a class="dropdown-item">{{ $t('events.by_mail') }}</a></li>
                            <li style="cursor: pointer;" @click="addGuestsByWhatsapp(participator)"><a class="dropdown-item">{{ $t('events.by_whatsapp') }}</a></li>
                            <li style="cursor: pointer;" @click="addGuestsByTwilio(participator)"><a class="dropdown-item">{{ $t('events.by_twilio') }}</a></li>
                        </ul>
                      </div>
                      <i class="bi bi-trash-fill fs-2" v-if="!isPublic" style="cursor: pointer;" @click.prevent="removeParticipator(participator.id)"></i>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4">
                      <div class="d-flex justify-content-center align-items-center" >
                        <div class="text-center">
                          <h5>{{ $t('events.notFound') }}</h5> 
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
        </div>
    </div>
    <div v-show="isPublic" class="card">
      <div class="card-body">
        <div class="col-md-8 mx-auto">
          <div class="d-flex flex-column justify-content-between align-items-center">
            <h1> {{ $t('events.planning.isPublic') }} </h1>
            <p class="text-gray-400 fs-4 fw-bold text-center"> {{ $t('events.planning.isPublicDesc') }} </p>
            <Button type="new" btn-class="btn-md ms-3" btn-text="button.edit" icon-class="fa fa-edit" @click.prevent="$emit('handleUpdatePlanning')"></Button>
          </div>
        </div>
      </div>
    </div>
  <AddParticipatorModal id="addParticipatorModal" :event-id="route.params.id" @created=""/>
</template>

<script setup>
import {ref, defineProps, computed, watch} from 'vue'
import Button from "@/components/Button";
import {useStore} from "vuex";
import Select2 from '@/components/Select2.vue';
import { useRoute } from 'vue-router';
import AddParticipatorModal from "@/Modules/Events/components/modals/AddParticipatorModal.vue";
import {useSwal} from "@/composables/useSwal";
import {$dateFormatter} from "@/mixins/Helpers";
import {useLocales} from "@/composables/locales";

const emits = defineEmits(["callback", "handleUpdatePlanning"]);

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  detail: {
    type: Object,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
})

const store = useStore()
const route = useRoute()
const {showSwal} = useSwal()
const { locale } = useLocales()

const formData = ref({
    event_id: '',
    invitation: [],
    guests: [],
    body: '',
    send_mail: ''
})
const selectParticipator = ref({})
const isLoading = ref(false)

const isPublic = computed(() => (props.detail.type_atelier === 'public'))
const guests = computed(() => {
  if(props.detail.atelier_guests && props.detail.atelier_guests.length > 0){
    return props.detail.atelier_guests.map(el => el.id)
  }
  return []
})
const participators = computed(() => store.getters['events/getEventParticipators'])
const participatorNotSelected = computed(() => {
  if (!participators.value) return []

  return participators.value.filter(p => !guests.value.some(inv => inv === p.id))
})
const event = computed(() => store.getters['events/getCurrentEvent'])

const messageToSend = computed(() => {
  if (!props.message) return '';

  let message = props.message;
  const matches = [...message.matchAll(/\{([^}]+)\}/g)];
  const variables = matches.map(match => match[1]);

  const gender = selectParticipator.value?.gender == 'female' ? i18n.global.t('user.female') : i18n.global.t('user.male');
  const date = $dateFormatter(props.detail?.date, locale.value);
  const eventDate = event.value?.dates?.find(item => item.day == props.detail?.date)

  variables.forEach((key) => {
    let replacement = '';
    switch (key) {
      case "PARTICIPATOR_NAME":
        replacement = selectParticipator.value?.fullname ?? '';
        break;
      case "CIVILITY":
        replacement = gender;
        break;
      case "COMPANY_NAME":
        replacement = selectParticipator.value?.company ?? '';
        break;
      case "QR_CODE":
        replacement = selectParticipator.value?.company ?? '';
        break;
      case "ATELIER_NAME":
        replacement = props.detail?.title ?? '';
        break;
      case "ATELIER_DATE":
        replacement = date ?? '';
        break;
      case "ATELIER_START":
        replacement = props.detail?.start_time ?? '';
        break;
      case "ATELIER_END":
        replacement = props.detail?.end_time ?? '';
        break;
      case "EVENT_TITLE":
        replacement = event.value?.title ?? '';
        break;
      case "EVENT_PLACE":
        replacement = event.value?.place ?? '';
        break;
      case "EVENT_START_DATE":
        replacement = `${$dateFormatter(eventDate?.day, locale.value) ?? ''} ${eventDate?.start ?? ''}`;
        break;
      case "EVENT_END_DATE":
        replacement = `${$dateFormatter(eventDate?.day, locale.value) ?? ''} ${eventDate?.end ?? ''}`;
        break;
      case "EVENT_DESCRIPTION":
        replacement = event.value?.description ?? '';
        break;
      case "EVENT_LONGITUDE":
        replacement = event.value?.longitude ?? '';
        break;
      case "EVENT_LATITUDE":
        replacement = event.value?.latitude ?? '';
        break;
      case "EVENT_SITE_LINK":
        replacement = event.value?.website_link ?? event.value?.website_link_default;
        break;
      case "EVENT_FORM_LINK":
        replacement = event.value?.invitation_link ?? '';
        break;
    }
    message = message.replaceAll(`{${key}}`, replacement);
  });

  return message.replaceAll('**', '*');
});

watch(() => props.detail, (value) => {
  if(props.detail) {
    loadData()
  }
})

watch(() => props.message, (value) => {
  if(value) {
    formData.value.body = value
  }
})

function loadData() {
  store.dispatch('planning/loadAtelierPrograms', props.detail?.type_id)
}

function convertHtmlToWhatsAppText(html) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<strong>(.*?)<\/strong>/gi, '*$1*')
    .replace(/<b>(.*?)<\/b>/gi, '*$1*')
    .replace(/<em>(.*?)<\/em>/gi, '_$1_')
    .replace(/<i>(.*?)<\/i>/gi, '_$1_')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<\/?[^>]+(>|$)/g, '') 
    .trim();
}

function addGuestsByWhatsapp(participator) {
  selectParticipator.value = participator;
  const participatorTel = participator.phone_code?.replace('+', '') + participator.phone?.replaceAll(' ', '');
  let text = convertHtmlToWhatsAppText(messageToSend.value)
  showSwal(
    i18n.global.t('events.by_whatsapp'),
    i18n.global.t('events.planning.swal_text')
  ).then((result) => {
    if (result.value) {
      const whatsappLink = `https://wa.me/${participatorTel}?text=${encodeURIComponent(text)}`;
      window.open(whatsappLink, '_blank');
    }
  });
}

function addGuestsByTwilio(participator) {
  selectParticipator.value = participator;
  const participatorTel = participator.phone_code?.replace('+', '') + participator.phone?.replaceAll(' ', '');
  let text = convertHtmlToWhatsAppText(messageToSend.value)
  const gender = participator?.gender == 'female' ? i18n.global.t('user.female') : i18n.global.t('user.male');

  let invitation = []
  invitation.push({
    phone: participatorTel,
    gender: gender,
    firstname: participator?.firstname,
    lastname: participator?.lastname
  })
  let data = {
    event_id: route.params.id,
    message: text,
    invitation: invitation
  }
  showSwal(
    i18n.global.t('events.by_twilio'),
    i18n.global.t('events.planning.swal_text')
  ).then((result) => {
    if (result.value) {
      store.dispatch('planning/sendInvitationGuestByTwilio', data)
        .then(() => {
          reloadPlanningData()
          window.toastr['success'](i18n.global.t('events.programs.added_guests_successfully'))
        })
    }
  })
}

function addGuestsByMail(id) {
  formData.value.body = props.message
  showSwal(
    i18n.global.t('events.by_mail'),
    i18n.global.t('events.planning.swal_text')
  ).then( async (result) => {
    if (result.value) {
      store.dispatch('planning/sendInvitationGuestByMail', {body: formData.value.body, atelier_id: props.detail?.type_id, event_id: route.params.id, guest_id: id})
      .then(() => {
        reloadPlanningData()
        window.toastr['success'](i18n.global.t('events.programs.added_guests_successfully'))
      })
    }
  })
}

function addParticipator() {
  isLoading.value = true
  formData.value.body = props.message
  store.dispatch('planning/addGuestsToAtelier', {...formData.value, atelier_id: props.detail?.type_id, event_id: route.params.id})
      .then(() => {
          isLoading.value = false
          formData.value = {
            event_id: route.params.id,
            invitation: [],
            guests: [],
            body: props.message,
            send_mail:''
          }
          reloadPlanningData()
          window.toastr['success'](i18n.global.t('events.programs.added_guests_successfully'))
      })
      .catch(() => {
          isLoading.value = false
      })
}

function removeParticipator(id) {
  store.dispatch('planning/removeGuestsToAtelier', {guests: [id], atelier_id: props.detail?.type_id})
      .then(() => {
        reloadPlanningData()
      })
}

function openCreateParticipatorModal() {
  $('#addParticipatorModal').modal('toggle');
}

function reloadPlanningData() {
  store.dispatch('planning/getEventPlanning', {id: route.params.planning, event_id: route.params.id})
}
</script>